import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { getUserProfile, UserProfile } from '../../services/userProfileService';

interface HeaderProps {
    showBack?: boolean;
    title?: string;
    transparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showBack = false, title, transparent = false }) => {
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [user, setUser] = useState<UserProfile | null>(null);

    useEffect(() => {
        const profile = getUserProfile();
        setUser(profile);
    }, []);

    if (!user) return null;

    // Hide header on game screen (use game's own header)
    if (location.pathname.startsWith('/play/')) {
        return null;
    }

    return (
        <>
            <header style={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                background: transparent ? 'transparent' : theme.colors.cardBg,
                borderBottom: transparent ? 'none' : `1px solid ${theme.colors.cardBorder}`,
                padding: '12px 16px',
                paddingTop: 'calc(12px + env(safe-area-inset-top))',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 999,
                backdropFilter: transparent ? 'none' : 'blur(10px)',
            }}>
                {/* Left side - Logo or Back */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {showBack ? (
                        <Link to="/" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            borderRadius: theme.borderRadius.full,
                            background: theme.colors.bgSecondary,
                            textDecoration: 'none',
                            fontSize: '18px',
                        }}>
                            ←
                        </Link>
                    ) : (
                        <Link to="/" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            textDecoration: 'none',
                        }}>
                            <span style={{ fontSize: '28px' }}>🧩</span>
                            <span style={{
                                fontFamily: theme.fonts.display,
                                fontSize: '20px',
                                fontWeight: 600,
                                color: theme.colors.textPrimary,
                            }} className="hide-mobile">
                                Puzzzeld
                            </span>
                        </Link>
                    )}
                    {title && (
                        <h1 style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: theme.colors.textPrimary,
                            margin: 0,
                        }}>
                            {title}
                        </h1>
                    )}
                </div>

                {/* Right side - Stats & Profile */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Daily Streak */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: theme.colors.bgSecondary,
                        borderRadius: theme.borderRadius.full,
                    }}>
                        <span style={{ fontSize: '16px' }}>🔥</span>
                        <span style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: theme.colors.accent,
                        }}>
                            {user.stats.streakDays}
                        </span>
                    </div>

                    {/* Coins */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        background: theme.colors.bgSecondary,
                        borderRadius: theme.borderRadius.full,
                    }} className="hide-mobile">
                        <span style={{ fontSize: '16px' }}>🪙</span>
                        <span style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: theme.colors.textPrimary,
                        }}>
                            {user.coins}
                        </span>
                    </div>

                    {/* Profile button */}
                    <button
                        onClick={() => setMenuOpen(!menuOpen)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '4px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: theme.borderRadius.full,
                            background: theme.gradients.warm,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            boxShadow: theme.shadows.sm,
                        }}>
                            🧩
                        </div>
                    </button>
                </div>
            </header>

            {/* Dropdown Menu */}
            {menuOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.3)',
                            zIndex: 1000,
                        }}
                        onClick={() => setMenuOpen(false)}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '70px',
                        right: '16px',
                        background: theme.colors.cardBg,
                        borderRadius: theme.borderRadius.lg,
                        boxShadow: theme.shadows.xl,
                        padding: '16px',
                        minWidth: '240px',
                        zIndex: 1001,
                        animation: 'slideDown 0.2s ease-out',
                    }} className="animate-slideDown">
                        {/* User Info */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            paddingBottom: '16px',
                            borderBottom: `1px solid ${theme.colors.cardBorder}`,
                            marginBottom: '12px',
                        }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: theme.borderRadius.full,
                                background: theme.gradients.warm,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '24px',
                            }}>
                                🧩
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                                    {user.name}
                                </div>
                                <div style={{
                                    fontSize: '13px',
                                    color: theme.colors.textSecondary,
                                }}>
                                    Level {user.level} • {user.xp} XP
                                </div>
                            </div>
                        </div>

                        {/* Menu Items */}
                        {[
                            { icon: '👤', label: 'View Profile', path: '/profile' },
                            { icon: '🎨', label: 'My Collection', path: '/collection' },
                            { icon: '📊', label: 'Statistics', path: '/stats' },
                            { icon: '⚙️', label: 'Settings', path: '/settings' },
                        ].map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setMenuOpen(false)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px',
                                    textDecoration: 'none',
                                    color: theme.colors.textPrimary,
                                    borderRadius: theme.borderRadius.md,
                                    transition: 'background 0.2s',
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bgSecondary}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <span style={{ fontSize: '20px' }}>{item.icon}</span>
                                <span style={{ fontWeight: 500 }}>{item.label}</span>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </>
    );
};

export default Header;
